import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { Add, Edit, Delete, ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

function Bancos() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [bancos, setBancos] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editando, setEditando] = useState(null);
  
  const [formData, setFormData] = useState({
    nombre_banco: '',
    tipo_cuenta: 'CORRIENTE',
    numero_cuenta: '',
    titular: '',
    saldo_inicial: 0
  });

  useEffect(() => {
    cargarBancos();
  }, []);

  const cargarBancos = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const response = await axios.get(`${API_URL}/api/tesoreria/bancos`, config);
      setBancos(response.data.data);
    } catch (err) {
      console.error('Error al cargar bancos:', err);
      setError('Error al cargar bancos');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (banco = null) => {
    if (banco) {
      setEditando(banco);
      setFormData({
        nombre_banco: banco.nombre_banco,
        tipo_cuenta: banco.tipo_cuenta,
        numero_cuenta: banco.numero_cuenta,
        titular: banco.titular || '',
        saldo_inicial: banco.saldo_inicial
      });
    } else {
      setEditando(null);
      setFormData({
        nombre_banco: '',
        tipo_cuenta: 'CORRIENTE',
        numero_cuenta: '',
        titular: '',
        saldo_inicial: 0
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditando(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      if (editando) {
        await axios.put(
          `${API_URL}/api/tesoreria/bancos/${editando.id}`,
          formData,
          config
        );
        setSuccess('Banco actualizado exitosamente');
      } else {
        await axios.post(`${API_URL}/api/tesoreria/bancos`, formData, config);
        setSuccess('Banco creado exitosamente');
      }

      handleCloseDialog();
      cargarBancos();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error al guardar banco:', err);
      setError(err.response?.data?.message || 'Error al guardar banco');
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleDelete = async (bancoId) => {
    if (!window.confirm('¿Está seguro de eliminar este banco?')) return;

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.delete(`${API_URL}/api/tesoreria/bancos/${bancoId}`, config);
      setSuccess('Banco eliminado exitosamente');
      cargarBancos();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error al eliminar banco:', err);
      setError(err.response?.data?.message || 'Error al eliminar banco');
      setTimeout(() => setError(null), 3000);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-EC', {
      style: 'currency',
      currency: 'USD'
    }).format(value || 0);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate('/tesoreria')} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
          Gestión de Bancos
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Nuevo Banco
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Banco</TableCell>
                <TableCell>Tipo Cuenta</TableCell>
                <TableCell>Número Cuenta</TableCell>
                <TableCell>Titular</TableCell>
                <TableCell align="right">Saldo Inicial</TableCell>
                <TableCell align="right">Saldo Actual</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!bancos || bancos.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No hay bancos registrados
                  </TableCell>
                </TableRow>
              ) : (
                bancos.map((banco) => (
                  <TableRow key={banco.id} hover>
                    <TableCell>
                      <Typography variant="body1" fontWeight="bold">
                        {banco.nombre_banco}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={banco.tipo_cuenta}
                        size="small"
                        color={banco.tipo_cuenta === 'CORRIENTE' ? 'primary' : 'secondary'}
                      />
                    </TableCell>
                    <TableCell>{banco.numero_cuenta}</TableCell>
                    <TableCell>{banco.titular || '-'}</TableCell>
                    <TableCell align="right">{formatCurrency(banco.saldo_inicial)}</TableCell>
                    <TableCell align="right">
                      <Typography 
                        variant="body1" 
                        fontWeight="bold"
                        color={banco.saldo_actual >= 0 ? 'success.main' : 'error.main'}
                      >
                        {formatCurrency(banco.saldo_actual)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={banco.activa ? 'Activa' : 'Inactiva'}
                        color={banco.activa ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(banco)}
                        color="primary"
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(banco.id)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Dialog Crear/Editar */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {editando ? 'Editar Banco' : 'Nuevo Banco'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Nombre del Banco"
                  value={formData.nombre_banco}
                  onChange={(e) => setFormData({ ...formData, nombre_banco: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Tipo de Cuenta</InputLabel>
                  <Select
                    value={formData.tipo_cuenta}
                    onChange={(e) => setFormData({ ...formData, tipo_cuenta: e.target.value })}
                    label="Tipo de Cuenta"
                  >
                    <MenuItem value="CORRIENTE">Corriente</MenuItem>
                    <MenuItem value="AHORROS">Ahorros</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Número de Cuenta"
                  value={formData.numero_cuenta}
                  onChange={(e) => setFormData({ ...formData, numero_cuenta: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Titular"
                  value={formData.titular}
                  onChange={(e) => setFormData({ ...formData, titular: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  type="number"
                  label="Saldo Inicial"
                  value={formData.saldo_inicial}
                  onChange={(e) => setFormData({ ...formData, saldo_inicial: parseFloat(e.target.value) })}
                  inputProps={{ step: 0.01, min: 0 }}
                  disabled={!!editando}
                  helperText={editando ? 'El saldo inicial no se puede modificar' : ''}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancelar</Button>
            <Button type="submit" variant="contained">
              {editando ? 'Actualizar' : 'Crear'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  );
}

export default Bancos;
